// client/src/components/admin/AdminUserDetails.js
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const AdminUserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    position: '',
    isActive: true,
    isApproved: true
  });
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);
  
  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const res = await axios.get(`/api/admin/users/${id}`);
        setUser(res.data.user);
        setFormData({
          firstName: res.data.user.firstName,
          lastName: res.data.user.lastName,
          email: res.data.user.email,
          phone: res.data.user.phone || '',
          position: res.data.user.position || '',
          isActive: res.data.user.isActive,
          isApproved: res.data.user.isApproved
        });
        setLoading(false);
      } catch (err) {
        console.error('Error fetching user:', err);
        setError('Failed to fetch user details');
        setLoading(false);
      }
    };
    
    fetchUserDetails();
  }, [id]);
  
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setActionInProgress(true);
    setError('');
    setSuccess('');
    
    try {
      const res = await axios.put(`/api/admin/users/${id}`, formData);
      setSuccess('User updated successfully');
      setUser(res.data.user);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update user');
    } finally {
      setActionInProgress(false);
    }
  };
  
  const handleDeleteUser = async () => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    setActionInProgress(true);
    
    try {
      await axios.delete(`/api/admin/users/${id}`);
      navigate('/admin/users', { replace: true });
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete user');
      setActionInProgress(false);
    }
  };
  
  const toggleUserStatus = async () => {
    setActionInProgress(true);
    setError('');
    setSuccess('');
    
    const newStatus = !formData.isActive;
    
    try {
      await axios.put(`/api/admin/users/${id}`, {
        ...formData,
        isActive: newStatus
      });
      
      setFormData({
        ...formData,
        isActive: newStatus
      });
      
      setSuccess(`User ${newStatus ? 'activated' : 'deactivated'} successfully`);
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess('');
      }, 3000);
    } finally {
      setActionInProgress(false);
    }
  };
  
  if (loading) {
    return <div className="loading">Loading user details...</div>;
  }
  
  if (error && !user) {
    return <div className="error-message">{error}</div>;
  }
  
  if (!user) {
    return <div className="error-message">User not found</div>;
  }
  
  return (
    <div className="admin-container">
      <div className="admin-header">
        <button 
          onClick={() => navigate(-1)} 
          className="btn btn-light"
        >
          &larr; Back
        </button>
        <h2 className="admin-page-title">User Details</h2>
      </div>
      
      {error && <div className="alert-danger">{error}</div>}
      {success && <div className="alert-success">{success}</div>}
      
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">User Information</h3>
          <div className="admin-status-badge">
            <span className={`status ${user.isActive ? 'status-approved' : 'status-rejected'}`}>
              {user.isActive ? 'Active' : 'Inactive'}
            </span>
            {!user.isApproved && (
              <span className="status status-pending ml-2">
                Pending Approval
              </span>
            )}
          </div>
        </div>
        <div className="admin-card-body">
          <form onSubmit={handleSubmit}>
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="firstName">First Name</label>
                <input
                  type="text"
                  id="firstName"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleInputChange}
                  className="admin-form-input"
                  required
                />
              </div>
              
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="lastName">Last Name</label>
                <input
                  type="text"
                  id="lastName"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleInputChange}
                  className="admin-form-input"
                  required
                />
              </div>
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="email">Email Address</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className="admin-form-input"
                  required
                />
              </div>
              
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="phone">Phone Number</label>
                <input
                  type="tel"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className="admin-form-input"
                />
              </div>
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="position">Position</label>
                <input
                  type="text"
                  id="position"
                  name="position"
                  value={formData.position}
                  onChange={handleInputChange}
                  className="admin-form-input"
                />
              </div>
              
              <div className="admin-form-group">
                <label className="admin-form-label" htmlFor="role">Role</label>
                <input
                  type="text"
                  id="role"
                  value={user.role}
                  className="admin-form-input"
                  disabled
                />
              </div>
            </div>
            
            <div className="admin-form-row">
              <div className="admin-form-group admin-checkbox-group">
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    name="isActive"
                    checked={formData.isActive}
                    onChange={handleInputChange}
                  />
                  User is active
                </label>
              </div>
              
              <div className="admin-form-group admin-checkbox-group">
                <label className="admin-checkbox-label">
                  <input
                    type="checkbox"
                    name="isApproved"
                    checked={formData.isApproved}
                    onChange={handleInputChange}
                  />
                  User is approved
                </label>
              </div>
            </div>
            
            <div className="admin-form-actions">
              <button
                type="submit"
                className="btn btn-primary"
                disabled={actionInProgress}
              >
                {actionInProgress ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
      
      {/* Organization Information */}
      {user.organization && (
        <div className="admin-card">
          <div className="admin-card-header">
            <h3 className="admin-card-title">Organization Information</h3>
            <Link to={`/admin/organizations/${user.organization._id}`} className="admin-view-btn">
              View Organization
            </Link>
          </div>
          <div className="admin-card-body">
            <div className="admin-detail-row">
              <div className="admin-detail-label">Name</div>
              <div className="admin-detail-value">{user.organization.name}</div>
            </div>
            
            <div className="admin-detail-row">
              <div className="admin-detail-label">Type</div>
              <div className="admin-detail-value">
                {user.organization.type === 'bank' ? 'Bank' : 'Company'}
              </div>
            </div>

            {/* Show Company Size for companies */}
            {user.organization.type === 'company' && (
            <div className="admin-detail-row">
              <div className="admin-detail-label">Company Size</div>
              <div className="admin-detail-value">{user.organization.companySize || 'Not specified'}</div>
            </div>
            )}
            
            <div className="admin-detail-row">
              <div className="admin-detail-label">Organization ID</div>
              <div className="admin-detail-value">{user.organization.organizationId}</div>
            </div>
            
            <div className="admin-detail-row">
              <div className="admin-detail-label">Status</div>
              <div className="admin-detail-value">
                <span className={`status ${user.organization.isActive ? 'status-approved' : 'status-rejected'}`}>
                  {user.organization.isActive ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            
            {user.organization.address && (
              <div className="admin-detail-row">
                <div className="admin-detail-label">Address</div>
                <div className="admin-detail-value">
                  {user.organization.address.street}, {user.organization.address.city}, {user.organization.address.postcode}, {user.organization.address.country}
                </div>
              </div>
            )}
            
            <div className="admin-detail-row">
              <div className="admin-detail-label">Contact Email</div>
              <div className="admin-detail-value">{user.organization.contactEmail}</div>
            </div>
            
            {user.organization.contactPhone && (
              <div className="admin-detail-row">
                <div className="admin-detail-label">Contact Phone</div>
                <div className="admin-detail-value">{user.organization.contactPhone}</div>
              </div>
            )}
          </div>
        </div>
      )}
      
      {/* User Activity */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">User Activity</h3>
        </div>
        <div className="admin-card-body">
          <div className="admin-detail-row">
            <div className="admin-detail-label">Registration Date</div>
            <div className="admin-detail-value">
              {new Date(user.createdAt).toLocaleDateString()}
            </div>
          </div>
          
          {user.lastLogin && (
            <div className="admin-detail-row">
              <div className="admin-detail-label">Last Login</div>
              <div className="admin-detail-value">
                {new Date(user.lastLogin).toLocaleString()}
              </div>
            </div>
          )}
        </div>
      </div>
      
      {/* Actions */}
      <div className="admin-card">
        <div className="admin-card-header">
          <h3 className="admin-card-title">Actions</h3>
        </div>
        <div className="admin-card-body">
          <div className="admin-actions-grid">
            <button
              className={`btn ${formData.isActive ? 'btn-danger' : 'btn-primary'}`}
              onClick={toggleUserStatus}
              disabled={actionInProgress}
            >
              {formData.isActive ? 'Deactivate User' : 'Activate User'}
            </button>
            
            <button
              className="btn btn-danger"
              onClick={handleDeleteUser}
              disabled={actionInProgress || user.role === 'admin'}
            >
              Delete User
            </button>
            
            <button
              className="btn btn-light"
              onClick={() => navigate('/admin/users')}
              disabled={actionInProgress}
            >
              Back to User List
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminUserDetails;